import { ReactElement, useEffect, useState } from 'react';
import { Box, CircularProgress, Paper } from '@mui/material';
import ApprovalGatesSection from './ApprovalGatesSection';
import { PoliciesService } from 'services';
import { Utils } from 'shared/helpers';
import { AISystemsService } from 'services/AISystemService';
import { useParams } from 'react-router-dom';

interface ApprovalGate {
  _id: string;
  gate_name: string;
  required_artifacts: string;
  reviewers: string;
  sort_order: number;
  organizationId: string;
  created_by: string;
  updated_by: string;
  deleted_by: string;
  deleted_at: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface AIApprovalGate {
  _id: string;
  ai_inventory_id: string;
  audit_gate_id: string;
  is_completed: boolean;
  completed_at: string | null;
  completed_by: {
    _id: string;
    email: string;
    name: string;
  } | null;
  due_date: string;
  notes: string;
  organizationId: string;
  created_by: string;
  updated_by: string | null;
  deleted_by: string | null;
  deleted_at: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface GateWithStatus {
  id: string;
  name: string;
  completed: boolean;
  completedDate?: string;
  dueDate?: string;
  required: string;
  reviewers: string;
  completedBy?: {
    name: string;
    email: string;
  };
  notes?: string;
  sort_order: number;
}

const ApprovalPanel = (): ReactElement => {
  const { aiSystemId } = useParams<{ aiSystemId: string }>();
  const [approvalGates, setApprovalGates] = useState<GateWithStatus[]>([]);
  const [loading, setLoading] = useState(true);

  const formatDate = (date?: string | null): string | undefined => {
    if (!date) return undefined;

    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const fetchApprovalData = async (): Promise<void> => {
    setLoading(true);
    try {
      const [allApprovalsRes, aiApprovalsRes] = await Promise.all([
        PoliciesService.getAllApprovals(),
        AISystemsService.getAISystemsApprovalGate(aiSystemId!),
      ]);

      const gatesData: ApprovalGate[] = allApprovalsRes.data.data || [];
      const aiGatesData: AIApprovalGate[] = aiApprovalsRes.data.data || [];

      const completedMap = new Map<string, AIApprovalGate>();
      aiGatesData.forEach((aiGate) => {
        if (aiGate.audit_gate_id) {
          completedMap.set(aiGate.audit_gate_id, aiGate);
        }
      });

      // Merge the data
      const mergedGates: GateWithStatus[] = gatesData.map((gate) => {
        const aiGate = completedMap.get(gate._id);
        const isCompleted = aiGate?.is_completed || false;

        return {
          id: gate._id,
          name: gate.gate_name,
          completed: isCompleted,
          completedDate: formatDate(aiGate?.completed_at),
          dueDate: formatDate(aiGate?.due_date),
          required: gate.required_artifacts || 'Nothing required',
          reviewers: gate.reviewers || 'Not assigned',
          completedBy: aiGate?.completed_by || undefined,
          notes: aiGate?.notes,
          sort_order: gate.sort_order,
        };
      });

      // Sort by sort_order
      mergedGates.sort((a, b) => a.sort_order - b.sort_order);

      setApprovalGates(mergedGates);
    } catch (error) {
      console.error('Failed to fetch approval data:', error);
      Utils.handleErrorResponse(error);
    } finally {
      setLoading(false);
    }
  };

  console.log(approvalGates, 'APPROVAL DATES');

  useEffect(() => {
    fetchApprovalData();
  }, []);

  const handleGateToggle = async (
    gateId: string,
    currentStatus: boolean
  ): Promise<void> => {
    const payload = {
      ai_inventory_id: aiSystemId,
      audit_gate_id: gateId,
      is_completed: currentStatus,
      due_date: null,
      notes: null,
    };

    AISystemsService.createUpdateAIApprovalGate(payload)
      .then(() => {
        fetchApprovalData();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  if (loading) {
    return (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={0} variant="outlined" className="p-4">
        <ApprovalGatesSection
          gates={approvalGates}
          onGateToggle={handleGateToggle}
          onRefresh={fetchApprovalData}
        />
      </Paper>

      {/* <Paper elevation={0} variant="outlined" className="mt-3 p-4">
        <ApprovalStatus />
      </Paper> */}
    </Box>
  );
};

export default ApprovalPanel;
